import React, { useEffect, useRef } from 'react';
import { Form, Input, Select } from 'antd';
import {
  createNews as createDownLoadNews,
  editNews as editDownLoadNews,
} from '../../../api';
import 'vditor/dist/index.css';
import '../style.vditor.scss';
import { messageError, messageSuccess } from '../../../components/message';
import getFormatMessage from '../../../locales/get-format-message';
import { IDownloadNews } from '../../../model/api-modules/download-center';
import { VDitorField } from './vditor-field';
import {
  getFirstManageSite,
  getLangFormatText,
  getSiteOptions,
  isMultiManageSite,
  LanguageType,
} from '../../../lib';

import { DeployArea } from '../../../model';
import { wrapRequestArea } from '../../../lib/http/lib';
import { omit, uniq } from 'lodash';
import { ConsoleDrawer } from '../../../components/drawer';
import { ConsoleForm } from '../../../components/form';
import { shortHash10 } from '../lib/hash';

interface IProps {
  readonly onClose: () => void;
  readonly onSave: () => void;
  readonly currentRecord: IDownloadNews | null;
}

interface IVDRef {
  validate: () => boolean;
  getValue: () => string;
}

export const DownloadNewsEdit = (props: IProps) => {
  const [form] = Form.useForm();

  const zhVDInstance = useRef<IVDRef>();
  const enVDInstance = useRef<IVDRef>();

  useEffect(() => {
    if (props.currentRecord) {
      const record = props.currentRecord;
      const { Title: title, site, allSiteRecord } = record;
      const formatTitle = getLangFormatText(title);
      const getSites = () => {
        if (allSiteRecord?.length) {
          return uniq(allSiteRecord.map((item) => item.site).filter(Boolean));
        }
        if (site) {
          return [site];
        }
        return [];
      };

      form.setFieldsValue({
        site: getSites(),
        zhTitle: formatTitle[LanguageType.zh_CN],
        enTitle: formatTitle[LanguageType.en_US],
        Description: props.currentRecord.Description,
      });
    }
  }, [props.currentRecord]);

  const onCancel = () => {
    props.onClose();
  };

  const onOk = async () => {
    await form.validateFields();
    const hasError =
      !zhVDInstance.current?.validate() || !enVDInstance.current?.validate();
    if (hasError) {
      return false;
    }
    const sites = form.getFieldValue('site') || [getFirstManageSite()];
    const values = omit(form.getFieldsValue(), 'site');
    values.Title = JSON.stringify({
      zh_CN: values.zhTitle || '',
      en_US: values.enTitle || '',
    });
    values.zhTitle = undefined;
    values.enTitle = undefined;
    values.Content = JSON.stringify({
      zh_CN: zhVDInstance.current?.getValue() || '',
      en_US: enVDInstance.current?.getValue() || '',
    });
    const res = await submitToSite(values, sites);
    if (res) {
      props.onSave();
      props.onClose();
      messageSuccess(getFormatMessage('operation_success'));
      return true;
    }
    messageError(getFormatMessage('operation_failed'));
    return false;
  };

  const submitToSite = async (values: any, sites: DeployArea[] = []) => {
    const { currentRecord } = props;
    const params = { ...values, Module: 1, Status: 1 };

    const dataSyncId = await shortHash10(
      `${values.Title}${1}${new Date().getTime()}`
    );
    const allRes = await Promise.all(
      sites.map(async (site) => {
        if (currentRecord) {
          const matchedId = currentRecord.allSiteRecord?.find(
            (item) => item.site === site
          )?.id;
          if (!matchedId) {
            return;
          }
          return await wrapRequestArea(
            () => editDownLoadNews(params, matchedId),
            site
          );
        }
        return await wrapRequestArea(
          () => createDownLoadNews({ ...params, DataSyncID: dataSyncId }),
          site
        );
      })
    );

    return allRes.every((item) => item === 0);
  };

  return (
    <ConsoleDrawer
      onClose={onCancel}
      onOk={onOk}
      title={
        props.currentRecord
          ? getFormatMessage('编辑下载中心通知')
          : getFormatMessage('创建下载中心通知')
      }
    >
      <ConsoleForm className="admin-form" form={form}>
        {isMultiManageSite() && (
          <Form.Item
            label={getFormatMessage('发布站点')}
            name="site"
            rules={[
              {
                required: true,
                message: getFormatMessage('请选择通知发布站点'),
              },
            ]}
          >
            <Select
              disabled={!!props.currentRecord}
              mode="multiple"
              options={getSiteOptions()}
              style={{ width: 300 }}
              placeholder={getFormatMessage('请选择通知发布站点')}
            />
          </Form.Item>
        )}
        <Form.Item
          label={getFormatMessage('zh_news_name')}
          name="zhTitle"
          rules={[
            {
              required: true,
              message: getFormatMessage('please_enter_news_name'),
            },
          ]}
        >
          <Input
            style={{ width: 300 }}
            placeholder={getFormatMessage('please_enter_news_name')}
          />
        </Form.Item>
        <Form.Item
          label={getFormatMessage('en_news_name')}
          name="enTitle"
          rules={[
            {
              required: true,
              message: getFormatMessage('please_enter_news_name'),
            },
          ]}
        >
          <Input
            style={{ width: 300 }}
            placeholder={getFormatMessage('please_enter_news_name')}
          />
        </Form.Item>
        <Form.Item
          label={getFormatMessage('description')}
          name="Description"
          rules={[
            { required: true, message: 'Please input your description!' },
          ]}
        >
          <Input
            style={{ width: 300 }}
            placeholder="Please input a description"
          />
        </Form.Item>

        <VDitorField
          initValue={
            getLangFormatText(props.currentRecord?.Content)[LanguageType.zh_CN]
          }
          ref={zhVDInstance}
          id="zhVditor"
          placeholder={getFormatMessage('please_update_zh_content')}
          contentExample="**内容示例**"
          label={getFormatMessage('cnContent')}
          name="cnContent"
        />

        <VDitorField
          initValue={
            getLangFormatText(props.currentRecord?.Content)[LanguageType.en_US]
          }
          ref={enVDInstance}
          id="enVditor"
          label={getFormatMessage('enContent')}
          name="enContent"
          placeholder={getFormatMessage('please_update_en_content')}
          contentExample="**Content Example**"
        />
      </ConsoleForm>
    </ConsoleDrawer>
  );
};
